/**
 * DE ENIGE PLEK WAAR COLLECTR BESLIST WAT ER NAAR SENTRY MAG.
 *
 * WAAROM DIT PAKKET BESTAAT — uit een echt, live lek van 09-08-2026. De maskering
 * bestond in VIER kopieën: `src/lib/sentry.ts` voor de Operator App en drie maal
 * `sentryPortal.ts` in elk van de drie portalen. Op 08-08 is er ÉÉN versterkt; de
 * andere drie bleven op de must-40-versie staan, en niets wees daarop.
 *
 * Het gevolg was niet theoretisch. Het debiteurportaal draagt zijn toegangstoken
 * in het PAD (`/d/:accessToken`), en de portaalversie stripte uitsluitend de
 * query (`url.split("?")[0]`). Een pad-token overleefde dat volledig, en de
 * gedeployde bundel van `dossier.collectr.be` rapporteerde met dat token naar het
 * PRODUCTIE-Sentry-project. Wie het event kon lezen, kon het dossier openen.
 *
 * Daarom is dit een PAKKET en geen vierde kopie met een pariteitstest ernaast:
 * een pariteitstest bewaakt dat kopieën gelijk blijven, maar hij kan niet
 * afdwingen dat een NIEUWE app er een gebruikt. Eén import kan dat wel.
 *
 * DE EDGES EN WORKERS draaien op Deno en kunnen deze workspace niet importeren;
 * die houden `supabase/functions/_shared/errorReporting.ts`, met de bestaande
 * pariteitstest in `npm run test:observability`.
 *
 * ONTWERPREGEL: dit bestand faalt DICHT. Alles wat vrije vorm kan dragen wordt
 * verwijderd in plaats van gefilterd — je vergeet nooit iets weg te gooien, je
 * vergeet wel iets te maskeren.
 */

const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
// Bound printed groups so a following sentence word is not swallowed.
const GROUPED_IBAN_RE = /\b(?:BI[0-9]{2}[ \u00a0\u202f][A-Z0-9]{5}[ \u00a0\u202f][A-Z0-9]{5}[ \u00a0\u202f][A-Z0-9]{11}[ \u00a0\u202f][A-Z0-9]{2}|LY[0-9]{2}[ \u00a0\u202f][A-Z0-9]{3}[ \u00a0\u202f][A-Z0-9]{3}[ \u00a0\u202f][A-Z0-9]{15}|[A-Z]{2}[0-9]{2}(?:[ \u00a0\u202f][A-Z0-9]{1,4}\b){3,8})\b/gi;
const IBAN_RE = /\b[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}\b/gi;
const PHONE_RE = /\+?\d[\d\s().-]{7,}\d/g;

/** Maskeert e-mailadressen, IBAN's en telefoonnummers in vrije tekst. */
export function scrubMessage(input: string): string {
  return input
    .replace(EMAIL_RE, "[email]")
    .replace(GROUPED_IBAN_RE, "[iban]")
    .replace(IBAN_RE, "[iban]")
    .replace(PHONE_RE, "[telefoon]");
}

/**
 * Padsegmenten die per definitie een geheim dragen. Het portaal routeert
 * `/d/:accessToken`; de deurwaarder heeft zijn eigen variant.
 */
const GEHEIME_PADPREFIXEN = new Set(["d", "deeplink", "toegang", "gdw", "bailiff"]);

/**
 * Ziet een segment eruit als een geheim in plaats van als een leesbaar woord?
 *
 * Bewust RUIM: liever een dossierreferentie onnodig gemaskeerd dan één token
 * gelekt. Een segment telt als geheim zodra het lang genoeg is en niet louter
 * uit letters bestaat — precies de vorm van een gegenereerd token, en niet die
 * van `instellingen` of `dossiers`.
 */
function lijktOpGeheim(segment: string): boolean {
  if (segment.length < 12) return false;
  if (/^[a-z-]+$/i.test(segment)) return false;
  return /[0-9]/.test(segment) || /[A-Z]/.test(segment) || segment.includes("_") || segment.includes(".");
}

/**
 * Maakt een URL veilig: query en fragment gaan er integraal af, en elk
 * padsegment dat een geheim kan zijn wordt vervangen.
 *
 * De query verdwijnt VOLLEDIG in plaats van gemaskeerd te worden: een
 * querystring is vrije vorm en er is geen enkele parameter die wij nodig hebben
 * om een fout te begrijpen.
 */
export function sanitizeUrl(input: string): string {
  if (typeof input !== "string" || input.length === 0) return input;

  let voor = input;
  let pad = "";

  const vraagteken = voor.search(/[?#]/);
  if (vraagteken >= 0) {
    voor = input.slice(0, vraagteken);
  }

  // Scheid schema+host van het pad zonder URL() te gebruiken: die gooit op
  // relatieve URL's, en een fout in de maskering mag nooit de rapportage breken.
  const schemaEinde = voor.indexOf("://");
  let basis = "";
  if (schemaEinde >= 0) {
    const naSchema = voor.indexOf("/", schemaEinde + 3);
    if (naSchema < 0) {
      basis = voor;
      pad = "";
    } else {
      basis = voor.slice(0, naSchema);
      pad = voor.slice(naSchema);
    }
  } else {
    pad = voor;
  }

  if (pad.length > 0) {
    const segmenten = pad.split("/");
    for (let i = 0; i < segmenten.length; i += 1) {
      const segment = segmenten[i] ?? "";
      if (segment.length === 0) continue;
      const vorige = i > 0 ? (segmenten[i - 1] ?? "").toLowerCase() : "";
      if (GEHEIME_PADPREFIXEN.has(vorige) || lijktOpGeheim(segment)) {
        segmenten[i] = "[token]";
      }
    }
    pad = segmenten.join("/");
  }

  return scrubMessage(basis + pad);
}

/**
 * URL-achtige stukken binnen VRIJE TEKST.
 *
 * Gevonden door de volledige-event-test tijdens het bouwen van deze fix: een
 * foutmelding luidt vaak "kon X niet laden: https://…/d/<token>". `scrubMessage`
 * maskeert alleen e-mail, IBAN en telefoon en liet dat token dus staan. Een
 * padtoken lekt daarmee alsnog, niet via `request.url` maar via de melding zelf.
 */
const URL_IN_TEKST_RE = /(?:https?:\/\/|\/)[^\s"'<>)\]]+/g;

/**
 * Maskering voor vrije tekst: eerst elke URL saneren, dan de losse
 * persoonsgegevens. Dit is wat er op elke tekst uit een event wordt losgelaten.
 */
export function scrubText(input: string): string {
  if (typeof input !== "string" || input.length === 0) return input;
  return scrubMessage(input.replace(URL_IN_TEKST_RE, (match) => sanitizeUrl(match)));
}

/**
 * Minimale, STRUCTURELE vorm van een Sentry-event; bewust geen SDK-import in dit
 * pakket, zodat het ook zonder `@sentry/react` toetsbaar is.
 *
 * Alle velden zijn optioneel en zonder index-signatuur: de SDK-typen (`ErrorEvent`,
 * `StackFrame`, `Breadcrumb`) dragen die evenmin, en een index-signatuur hier maakt
 * ze onverenigbaar. Gevonden door `tsc -b`, niet door te lezen.
 */
type FrameAchtig = {
  filename?: unknown;
  abs_path?: unknown;
  pre_context?: unknown;
  context_line?: unknown;
  post_context?: unknown;
  vars?: unknown;
};

type BreadcrumbAchtig = {
  message?: unknown;
  data?: unknown;
};

export type SentryEventachtig = {
  user?: { id?: unknown; email?: unknown; ip_address?: unknown; username?: unknown } | null;
  message?: unknown;
  exception?: { values?: Array<{ value?: unknown; stacktrace?: { frames?: FrameAchtig[] } }> };
  breadcrumbs?: BreadcrumbAchtig[] | null;
  extra?: unknown;
  contexts?: Record<string, unknown> | null;
  tags?: Record<string, unknown> | null;
  request?: {
    url?: unknown;
    data?: unknown;
    cookies?: unknown;
    headers?: unknown;
    query_string?: unknown;
  } | null;
};

/**
 * Contexts die de SDK zelf zet en die géén persoonsgegevens dragen. Alles wat
 * hier niet in staat is door onze eigen code gezet en gaat er dus af — de
 * allow-listregel, ook op dit niveau.
 */
const TOEGESTANE_CONTEXTS = new Set(["browser", "os", "device", "runtime", "app", "culture", "trace"]);

/**
 * Het laatste vangnet, gedeeld door alle vier de applicaties.
 *
 * Werkt IN PLAATS op het event en geeft het terug, zodat hij rechtstreeks als
 * `beforeSend` kan dienen.
 */
export function scrubSentryEvent<T extends SentryEventachtig>(event: T): T {
  // 1 · gebruiker: hooguit een id, nooit een adres of naam.
  if (event.user) {
    const id = event.user.id;
    event.user = typeof id === "string" && id !== "" ? { id } : {};
  }

  // 2 · foutmelding en stacktrace.
  for (const waarde of event.exception?.values ?? []) {
    if (typeof waarde.value === "string") waarde.value = scrubText(waarde.value);
    for (const frame of waarde.stacktrace?.frames ?? []) {
      if (typeof frame.filename === "string") frame.filename = sanitizeUrl(frame.filename);
      if (typeof frame.abs_path === "string") frame.abs_path = sanitizeUrl(frame.abs_path);
      // Broncontext en lokale variabelen dragen formulierwaarden.
      delete frame.pre_context;
      delete frame.context_line;
      delete frame.post_context;
      delete frame.vars;
    }
  }

  if (typeof event.message === "string") event.message = scrubText(event.message);

  // 3 · breadcrumbs. `data` draagt fetch-URL's, console-argumenten en
  //     history-overgangen — en dus precies het pad-token.
  for (const kruimel of event.breadcrumbs ?? []) {
    if (typeof kruimel.message === "string") kruimel.message = scrubText(kruimel.message);
    delete kruimel.data;
  }

  // 4 · vrije vorm: weg.
  delete event.extra;

  // 5 · contexts: alleen wat de SDK zelf zet.
  if (event.contexts) {
    for (const sleutel of Object.keys(event.contexts)) {
      if (!TOEGESTANE_CONTEXTS.has(sleutel)) delete event.contexts[sleutel];
    }
  }

  // 6 · tags zijn sleutel/waarde en kunnen door een directe SDK-aanroep vrije
  //     tekst dragen; maskeren in plaats van verwijderen, want ze zijn nuttig.
  if (event.tags) {
    for (const [sleutel, waarde] of Object.entries(event.tags)) {
      if (typeof waarde === "string") event.tags[sleutel] = scrubText(waarde);
    }
  }

  // 7 · request: alles wat inhoud kan dragen eraf, URL gesaneerd.
  if (event.request) {
    delete event.request.data;
    delete event.request.cookies;
    delete event.request.headers;
    delete event.request.query_string;
    if (typeof event.request.url === "string") event.request.url = sanitizeUrl(event.request.url);
  }

  return event;
}

/**
 * Sentry-opties die voor alle vier de applicaties gelijk moeten zijn.
 *
 * `tracesSampleRate: 0` is een founderbesluit (g4-c4, 04-08-2026): performance
 * verbruikt een eigen quota naast de fouten, en dit criterium gaat over fouten.
 */
export const GEDEELDE_SENTRY_OPTIES = {
  sendDefaultPii: false,
  tracesSampleRate: 0,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
} as const;
